import { InputPrice } from '@/components/element/Form/InputPrice';
import { useUpdateBillingAmount } from '@/hooks/api/1970_update_billing_amount';
import { toastApiError } from '@/lib/toast';
import { useCurrentInvoiceTask } from '@/providers/InvoiceTaskProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ConsumptionTaxRuleLogic } from '@/hooks/api/models/logics';

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  task_id: number;
  billing_amount: number;
  billing_amount_including_tax?: number;
};

export const PaymentAmount = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { invoiceTask: task, setInvoiceTask } = useCurrentInvoiceTask();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, isSuccess, error, isPending } = useUpdateBillingAmount();
  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      billing_amount: Number(task!.billing_amount),
      billing_amount_including_tax: Number(task!.billing_amount_including_tax),
    },
  });

  const [billingAmountIncludingTax] = watch(['billing_amount_including_tax']);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (isSuccess && data) {
      setEditing(false);
      setInvoiceTask(data);
    }
  }, [isSuccess]);

  return (
    <div
      className={`checklist-item ps-2 ms-3 ${
        task!.billing_amount != null ? 'checklist-item-success' : 'checklist-item-secondary'
      }`}
    >
      <div className="ms-1">
        <h6 className="mb-0 text-dark font-weight-bold text-sm">請求金額の確定</h6>
        <div className="text-weak text-xs">請求書に記載された金額を入力して下さい</div>
      </div>

      <div className="d-flex align-items-center ms-1 mt-3 ps-1">
        <table className="table align-items-center">
          <thead>
            <tr>
              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
                合計金額（税込）
              </th>
              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
                合計金額（税抜）
              </th>
              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
                計画金額（税抜）
              </th>
              <th className="text-secondary opacity-7" style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {!editing && (
              <tr>
                <td className="text-xs text-dark text-end">
                  {task!.billing_amount_including_tax != null
                    ? `¥${task!.billing_amount_including_tax.toLocaleString()}`
                    : '-'}
                </td>
                <td className="text-xs text-dark text-end">
                  {task!.billing_amount != null ? `¥${task!.billing_amount.toLocaleString()}` : '-'}
                </td>
                <td className="text-xs text-dark text-end">¥{task!.billing_schedule_detail.amount.toLocaleString()}</td>
                <td className="pe-0 text-end">
                  <button
                    className={`btn btn-icon-only bg-gradient-success ${disabled && 'disabled'}`}
                    onClick={() => setEditing((e) => !e)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                </td>
              </tr>
            )}
            {editing && (
              <tr>
                <td className="text-xs">
                  <div className="input-group">
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <InputPrice onChange={onChange} value={value} error={errors.billing_amount_including_tax} />
                      )}
                      control={control}
                      name="billing_amount_including_tax"
                      rules={{ required: true }}
                    />
                  </div>
                </td>
                <td className="text-xs text-wrap">
                  <div className="input-group">
                    <Controller
                      render={({ field: { onChange, value, ...field } }) => (
                        <InputPrice
                          onChange={onChange}
                          onBlur={(val) => {
                            if (val != null && Number(val) > 0 && billingAmountIncludingTax == 0) {
                              setValue(
                                'billing_amount_including_tax',
                                ConsumptionTaxRuleLogic.calc(Number(val), _w_!.consumption_tax_rule)
                              );
                            }
                          }}
                          value={value}
                          error={errors.billing_amount}
                        />
                      )}
                      control={control}
                      name="billing_amount"
                      rules={{ required: true }}
                    />
                  </div>
                </td>
                <td className="text-xs text-dark text-end">¥{task!.billing_schedule_detail.amount.toLocaleString()}</td>
                <td className="text-end">
                  <button
                    className="btn btn-icon-only btn-outline-secondary me-1"
                    onClick={() => setEditing((e) => !e)}
                  >
                    <i className="fas fa-undo"></i>
                  </button>
                  <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
                    <i className="fas fa-check-circle"></i>
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
